import {
  Button,
  MenuItem,
  Paper,
  Select,
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextareaAutosize,
  TextField,
} from "@mui/material";
import {Box} from "@mui/system";
import React, {useEffect} from "react";
import {Controller, useFieldArray, useForm} from "react-hook-form";
import {useNavigate, useParams} from "react-router-dom";
import {useRecoilState, useRecoilValue, useSetRecoilState} from "recoil";
import * as questionApi from "../../../api/educationApi/questionApi";
import ActionButtonGroup from "../../../components/ActionButtonGroup";
import ConfirmButtonGroup from "../../../components/ConfirmButtonGroup";
import MWProgress from "../../../components/MWProgress";
import SubTitle from "../../../components/SubTitle";
import {useCreateMutation, useGetItemQuery, useUpdateMutation} from "../../../hooks/useQueryHooks";
import {editExamActionButton} from "../../../models/clientModels/data/actionButtonData";
import {editExamTableHead} from "../../../models/clientModels/data/tableHeaderData";
import * as Params from "../../../models/clientModels/interfaces/apiParams";
import {ResponseList, ResponseFile} from "../../../models/clientModels/interfaces/Response";
import {EDQuestion, initializeQuestion} from "../../../models/serverModels/education/EDQuestion";
import {alertStateAtom, extraAlertStateAtom} from "../../../Recoil/Atom/AlertAtom";
import {tokenSelector} from "../../../Recoil/Atom/authState";
import {snackBarState, snackBarToggleState} from "../../../Recoil/Atom/snackBarState";
import {SubMenuGroup} from "../../../styledComponents/StyledListContainer";
import {ReactComponent as ExcelIcon} from "../../../assets/images/excel.svg";
import {useQueryClient} from "react-query";

const EditExamContainer = styled(Paper)(({theme}) => ({
  backgroundColor: theme.palette.background.default,
}));
const StyledTableHead = styled(TableHead)(({theme}) => ({
  backgroundColor: theme.palette.gray.dark,
}));

const Textarea = styled(TextareaAutosize)(({theme}) => ({
  width: "100%",
  padding: "8px",
  lineHeight: 1.4,
  resize: "none",
}));

type FormValues = {
  question: EDQuestion[];
};

const EditExam = () => {
  let {id} = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const tokenState = useRecoilValue(tokenSelector);

  const setAlertState = useSetRecoilState(alertStateAtom);

  const setSnackBarState = useSetRecoilState(snackBarState);
  const setSnackBarToggle = useSetRecoilState(snackBarToggleState);

  const [extraAlertState, setExtraAlertState] = useRecoilState(extraAlertStateAtom);

  const {
    data: question,
    isLoading,
    isFetching,
  } = useGetItemQuery<ResponseList<EDQuestion>>({
    key: ["getEditExam", {id, token: tokenState}],
    api: () => questionApi.getList({id, token: tokenState}),
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: {errors},
  } = useForm<FormValues>({
    defaultValues: {question: question?.items},
  });

  const {fields, append, remove, update} = useFieldArray({
    name: "question",
    control,
  });

  const {mutate: updateMutate} = useUpdateMutation<
    ResponseList<EDQuestion>,
    Params.UpdateList<EDQuestion>
  >({
    api: questionApi.update,
    options: {
      onSuccess: () => {
        setSnackBarToggle(true);
        setSnackBarState("update");
        navigate("/Education/curriculumList");
      },
    },
  });

  const {mutate: loadMutate} = useCreateMutation<ResponseFile, Params.ImportFile>({
    api: questionApi.ImportQuestions,
    options: {
      onSuccess: (data: ResponseFile) => {
        setExtraAlertState({...extraAlertState, toggle: true, message: data.extra});
        queryClient.invalidateQueries("getEditExam");
      },
      onError: (error: Error) => {
        setExtraAlertState({...extraAlertState, toggle: true, message: error?.message});
      },
    },
  });

  useEffect(() => {
    let values = {question: question?.items};
    reset(values);
  }, [question?.items, reset]);

  const onSubmit = (data: FormValues) => {
    updateMutate({
      id: parseInt(id!),
      token: tokenState,
      items: data.question,
    });
  };

  const updateHandler = () => {
    append(initializeQuestion);
  };

  return (
    <React.Fragment>
      <SubTitle subTitle="문항관리" />
      <SubMenuGroup>
        <Box></Box>
        <ActionButtonGroup data={editExamActionButton(updateHandler)}>
          <Button
            variant="outlined"
            size="medium"
            component="label"
            startIcon={<ExcelIcon width="20px" height="20px" />}
            color="success"
            sx={{whiteSpace: "nowrap", flexShrink: 0, fontSize: "12px"}}
          >
            Excel 불러오기
            <TextField
              sx={{display: "none"}}
              onChange={async (event: React.ChangeEvent<HTMLInputElement>) => {
                const convertBase64 = (data: Blob): Promise<any> => {
                  return new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.readAsDataURL(data);
                    reader.onload = () => {
                      resolve(reader.result);
                    };
                    reader.onerror = (error) => {
                      reject(error);
                    };
                  });
                };
                const file = event.target?.files![0];
                const base64 = await convertBase64(file);
                loadMutate({token: tokenState, base64: base64.split(",")[1], id: parseInt(id!)});
              }}
              type="file"
              id="base64"
              inputProps={{
                accept:
                  ".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel",
              }}
            />
          </Button>
        </ActionButtonGroup>
      </SubMenuGroup>
      <form onSubmit={handleSubmit(onSubmit)}>
        {isLoading && isFetching ? (
          <MWProgress />
        ) : (
          <EditExamContainer>
            <Table>
              <StyledTableHead>
                <TableRow>
                  {editExamTableHead.map((item, index) => (
                    <TableCell key={index} align="center">
                      {item}
                    </TableCell>
                  ))}
                </TableRow>
              </StyledTableHead>
              <TableBody>
                {fields?.map((item, index) => (
                  <TableRow key={item.id}>
                    <TableCell align="center">{index + 1}</TableCell>
                    <TableCell align="center">
                      <Controller
                        name={`question.${index}.question`}
                        control={control}
                        render={({field, fieldState: {error}}) => (
                          <>
                            <Textarea
                              {...field}
                              minRows={4}
                              required
                              placeholder="문제를 입력하세요"
                            />
                          </>
                        )}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Controller
                        name={`question.${index}.answer1`}
                        control={control}
                        render={({field}) => (
                          <Textarea
                            {...field}
                            minRows={4}
                            required
                            placeholder="문항을 입력하세요"
                          />
                        )}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Controller
                        name={`question.${index}.answer2`}
                        control={control}
                        render={({field}) => (
                          <Textarea
                            {...field}
                            minRows={4}
                            required
                            placeholder="문항을 입력하세요"
                          />
                        )}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Controller
                        name={`question.${index}.answer3`}
                        control={control}
                        render={({field}) => (
                          <Textarea
                            {...field}
                            aria-label="minimum height"
                            minRows={4}
                            required
                            placeholder="문항을 입력하세요"
                          />
                        )}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Controller
                        name={`question.${index}.answer4`}
                        control={control}
                        render={({field}) => (
                          <Textarea
                            {...field}
                            aria-label="minimum height"
                            minRows={4}
                            required
                            placeholder="문항을 입력하세요"
                          />
                        )}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Controller
                        name={`question.${index}.rightness`}
                        control={control}
                        render={({field}) => (
                          <Select {...field} id="departmentUID" size="small">
                            <MenuItem value={1}>1</MenuItem>
                            <MenuItem value={2}>2</MenuItem>
                            <MenuItem value={3}>3</MenuItem>
                            <MenuItem value={4}>4</MenuItem>
                          </Select>
                        )}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        variant="contained"
                        color="error"
                        size="small"
                        onClick={() => {
                          setAlertState({
                            toggle: true,
                            message: "문항을 삭제하시겠습니까?",
                            execution: () => {
                              remove(index);
                              console.log(index);
                            },
                          });
                        }}
                      >
                        삭제
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </EditExamContainer>
        )}
        <ConfirmButtonGroup
          link="/Education/CurriculumList"
          cancelText="취소"
          confirmText="문항등록"
        />
      </form>
    </React.Fragment>
  );
};

export default EditExam;
